import type { ReactElement } from "react";
import type { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import type { MaterialNames } from "@/components/material_symbol_names";
import type { Path } from "@/router";
import { buildPATStatusForNonMembers } from "@/features/person/utils/utils";
import { getConsolidatedPATStatusLabel } from "@/features/person/utils/pat-status";
import {
  APPLICATION_REQUIREMENT_METRICS_BY_TYPE,
  type ApplicationRequirementMetricId,
  type ApplicationType
} from "../../types";
import type {
  AffiliateApplicationEligibility,
  ApplicationPgmProgressStatus,
  ApplicationPlayingAbilityTestStatus,
  AssociateApplicationEligibility,
  MemberApplicationEligibility
} from "../queries";
import type { BackgroundCheck, ExperienceCredits, PlayingAbilityTestStatus, QualifyingLevel } from "@/types";

const PASS_HEADLINE = {
  completed: "Completed",
  passed: "Passed",
  paid: "Paid"
} as const;

type PassHeadlineType = keyof typeof PASS_HEADLINE;

interface RequirementConfig {
  label: string;
  iconName: MaterialNames;
  passHeadline: PassHeadlineType;
}

const REQUIREMENT_CONFIG: Record<ApplicationRequirementMetricId, RequirementConfig> = {
  pat: {
    label: "Playing Ability Test (PAT)",
    iconName: "sports_golf",
    passHeadline: "passed"
  },
  pgm_associate: {
    label: "PGM Associate 3.1",
    iconName: "school",
    passHeadline: "completed"
  },
  background_check: {
    label: "Background Check",
    iconName: "fingerprint",
    passHeadline: "passed"
  },
  experience_credits: {
    label: "Work Experience Credits",
    iconName: "business_center",
    passHeadline: "completed"
  },
  dues: {
    label: "Dues Status (Balance)",
    iconName: "receipt_long",
    passHeadline: "paid"
  }
};

function pgmRequirementLabel(applicationType?: ApplicationType): string {
  if (applicationType === "associate" || applicationType === "affiliate") {
    return "Qualifying Level";
  }
  return REQUIREMENT_CONFIG.pgm_associate.label;
}

function openRouteInNewTab(path: string) {
  window.open(path, "_blank", "noopener,noreferrer");
}

function isQualifyingLevelExpired(qualifyingLevel: QualifyingLevel): boolean {
  return qualifyingLevel.status?.toUpperCase() === "EXPIRED";
}

function isQualifyingLevelComplete(
  qualifyingLevel?: QualifyingLevel | null,
  pgmProgressStatus?: ApplicationPgmProgressStatus | null
): boolean {
  if (qualifyingLevel) {
    if (isQualifyingLevelExpired(qualifyingLevel)) return false;

    if (
      !!qualifyingLevel.endDate ||
      !!qualifyingLevel.completed ||
      qualifyingLevel.status?.toUpperCase() === "COMPLETED"
    ) {
      return true;
    }
  }

  if (pgmProgressStatus?.mostRecentCompletedLevelKey === "qualifying") return true;

  const qualifyingProgress = pgmProgressStatus?.levels?.find((level) => level.key === "qualifying");
  if (qualifyingProgress?.status === "complete") return true;

  return false;
}

function normalizePgmLevelKey(key: string): string {
  return key.replace(/_/g, "");
}

function isLevel3ProgressLevel(level: ApplicationPgmProgressStatus["levels"][number]): boolean {
  return level.numeral === 3 || normalizePgmLevelKey(level.key) === "level3";
}

function isMemberLevel3Complete(pgmProgressStatus?: ApplicationPgmProgressStatus | null): boolean {
  const level3 = pgmProgressStatus?.levels?.find(isLevel3ProgressLevel);
  return level3?.status === "complete";
}

function isApplicationPatRequirementMet(
  patData: ApplicationPlayingAbilityTestStatus | null | undefined,
  applicationType?: ApplicationType
): boolean {
  if (!patData || patData.activeSuspension || patData.expired || !patData.completed) return false;

  const status = patData.status?.toLowerCase();

  if (applicationType === "associate") {
    return status === "passed" || status === "qualified";
  }

  return patData.completed === true && status === "passed" && !patData.activeSuspension && !patData.expired;
}

function pluralizeCredits(credits: number): string {
  return `${credits} ${credits === 1 ? "Credit" : "Credits"}`;
}

type ExperienceCreditsTooltipSource = {
  total: number;
  threshold?: number | null;
  educationCredits?: number | null;
  employmentCredits?: number | null;
};

function buildExperienceCreditLines(experienceCredits: ExperienceCreditsTooltipSource) {
  return [
    experienceCredits.educationCredits
      ? {
          id: "education",
          description: "Academic Degree",
          credits: experienceCredits.educationCredits
        }
      : null,
    experienceCredits.employmentCredits
      ? {
          id: "employment",
          description: "Employment",
          credits: experienceCredits.employmentCredits
        }
      : null
  ].filter((line): line is { id: string; description: string; credits: number } => line !== null);
}

function resolveExperienceCreditsTooltipSource(
  applicationType: ApplicationType | undefined,
  input: RequirementMetricInput
): ExperienceCreditsTooltipSource | null {
  if (applicationType === "member" && input.memberApplicationEligibility) {
    const { total, threshold } = input.memberApplicationEligibility.experienceCredits;
    return { total, threshold };
  }
  return input.experienceCredits ?? null;
}

function buildExperienceCreditsTooltip(experienceCredits: ExperienceCreditsTooltipSource) {
  const creditLines = buildExperienceCreditLines(experienceCredits);

  return {
    title: "Work Experience Credits",
    supportingText: (
      <div className="flex w-full flex-col items-start gap-1 text-body-medium text-sys-brand-on-surface-variant">
        <div>
          Total Credits: {experienceCredits.total}
          {experienceCredits.threshold != null ? ` (${experienceCredits.threshold}+ required)` : ""}
        </div>
        {creditLines.length > 0 ? (
          <ul className="list-disc pl-5">
            {creditLines.map((line) => (
              <li key={line.id}>
                {line.description} - {pluralizeCredits(line.credits)}
              </li>
            ))}
          </ul>
        ) : experienceCredits.total === 0 ? (
          <div>No experience credits have been recorded yet</div>
        ) : null}
      </div>
    ) as ReactElement
  };
}

function buildPatTooltip(patData: ApplicationPlayingAbilityTestStatus | null | undefined) {
  if (!patData) return undefined;

  // buildPATStatusForNonMembers only reads the PAT fields fetched by MEMBERSHIP_APPLICATION_QUERY.
  const patStatus = buildPATStatusForNonMembers(patData as PlayingAbilityTestStatus);
  const isInProgressOrQualifying =
    patStatus.value === getConsolidatedPATStatusLabel("qualifying") ||
    patStatus.value === getConsolidatedPATStatusLabel("in_progress");

  if (isInProgressOrQualifying) {
    return {
      supportingText: "Qualifying score required to begin Associate Program\nPassing score required for Member Election"
    };
  }

  if (
    patStatus.value !== getConsolidatedPATStatusLabel("failed") &&
    patStatus.value !== getConsolidatedPATStatusLabel("expired") &&
    patStatus.tooltip
  ) {
    return patStatus.tooltip;
  }

  return undefined;
}

export interface RequirementMetricInput {
  applicationType?: ApplicationType;
  experienceCredits?: ExperienceCredits | null;
  backgroundCheck?: BackgroundCheck | null;
  qualifyingLevel?: QualifyingLevel | null;
  pgmProgressStatus?: ApplicationPgmProgressStatus | null;
  playingAbilityTestStatus?: ApplicationPlayingAbilityTestStatus | null;
  duesBalance?: number | null;
  /** From Backoffice eligibility fields; null when type mismatches. */
  associateApplicationEligibility?: AssociateApplicationEligibility | null;
  affiliateApplicationEligibility?: AffiliateApplicationEligibility | null;
  memberApplicationEligibility?: MemberApplicationEligibility | null;
}

function evaluateRequirementPass(
  id: ApplicationRequirementMetricId,
  applicationType: ApplicationType | undefined,
  input: RequirementMetricInput
): boolean {
  switch (id) {
    case "pat": {
      if (applicationType === "associate" && input.associateApplicationEligibility) {
        return input.associateApplicationEligibility.playingAbilityTest.complete;
      }
      return isApplicationPatRequirementMet(input.playingAbilityTestStatus, applicationType);
    }
    case "pgm_associate": {
      if (applicationType === "affiliate" && input.affiliateApplicationEligibility) {
        return input.affiliateApplicationEligibility.qualifyingLevel.complete;
      }
      if (applicationType === "associate" && input.associateApplicationEligibility) {
        return input.associateApplicationEligibility.qualifyingLevel.complete;
      }
      if (applicationType === "member") {
        return isMemberLevel3Complete(input.pgmProgressStatus);
      }
      return isQualifyingLevelComplete(input.qualifyingLevel, input.pgmProgressStatus);
    }
    case "background_check": {
      if (applicationType === "affiliate" && input.affiliateApplicationEligibility) {
        return input.affiliateApplicationEligibility.backgroundCheck.complete;
      }
      if (applicationType === "associate" && input.associateApplicationEligibility) {
        return input.associateApplicationEligibility.backgroundCheck.complete;
      }
      return input.backgroundCheck?.status?.toUpperCase() === "APPROVED";
    }
    case "experience_credits": {
      if (applicationType === "member" && input.memberApplicationEligibility) {
        return input.memberApplicationEligibility.experienceCredits.complete;
      }
      return input.experienceCredits?.complete === true;
    }
    case "dues": {
      if (applicationType === "associate" && input.associateApplicationEligibility) {
        return input.associateApplicationEligibility.dues.complete;
      }
      return input.duesBalance === 0;
    }
    default: {
      const _exhaustive: never = id;
      return _exhaustive;
    }
  }
}

function buildPassFailMetric(
  config: RequirementConfig,
  label: string,
  passed: boolean,
  options: {
    onClick?: () => void;
    tooltip?: MetricCardType<Path>["tooltip"];
  }
): MetricCardType<Path> {
  return {
    iconName: config.iconName,
    label,
    value: PASS_HEADLINE[config.passHeadline],
    variant: passed ? "success" : "error",
    ...(options.tooltip && { tooltip: options.tooltip }),
    ...(options.onClick && { onClick: options.onClick })
  };
}

function buildRequirementMetricById(
  id: ApplicationRequirementMetricId,
  pgaId: string,
  applicationType: ApplicationType | undefined,
  input: RequirementMetricInput
): MetricCardType<Path> {
  const config = REQUIREMENT_CONFIG[id];
  const passed = evaluateRequirementPass(id, applicationType, input);
  const label = id === "pgm_associate" ? pgmRequirementLabel(applicationType) : config.label;

  switch (id) {
    case "pat":
      return buildPassFailMetric(config, label, passed, {
        onClick: () => openRouteInNewTab(`/people/${pgaId}/membership/prospect-program?scrollTo=pat-activity-table`),
        tooltip: buildPatTooltip(input.playingAbilityTestStatus)
      });
    case "pgm_associate":
      return buildPassFailMetric(config, label, passed, {
        onClick: () => openRouteInNewTab(`/people/${pgaId}/membership/path-to-membership`)
      });
    case "background_check":
      return buildPassFailMetric(config, label, passed, {
        onClick: () => openRouteInNewTab(`/people/${pgaId}/membership/prospect-program`)
      });
    case "experience_credits": {
      const tooltipSource = resolveExperienceCreditsTooltipSource(applicationType, input);
      return buildPassFailMetric(config, label, passed, {
        onClick: () => openRouteInNewTab(`/people/${pgaId}/membership/path-to-membership`),
        tooltip: tooltipSource ? buildExperienceCreditsTooltip(tooltipSource) : undefined
      });
    }
    case "dues":
      return buildPassFailMetric(config, label, passed, {
        onClick: () => openRouteInNewTab(`/people/${pgaId}/billing/orders`)
      });
    default: {
      const _exhaustive: never = id;
      return _exhaustive;
    }
  }
}

export function buildRequirementMetrics(pgaId: string, input: RequirementMetricInput): MetricCardType<Path>[] {
  const { applicationType } = input;
  if (!applicationType) return [];

  const requirementIds = APPLICATION_REQUIREMENT_METRICS_BY_TYPE[applicationType];

  return requirementIds.map((id) => buildRequirementMetricById(id, pgaId, applicationType, input));
}

export const REQUIREMENT_METRICS_GRID_COLS: Record<number, string> = {
  2: "md:[&>[data-testid=metrics]]:grid-cols-2",
  3: "md:[&>[data-testid=metrics]]:grid-cols-3",
  4: "md:[&>[data-testid=metrics]]:grid-cols-4",
  5: "md:[&>[data-testid=metrics]]:grid-cols-5"
};
